import React from 'react';
import { useMatch } from '../context/MatchContext';
import { calculateAthleteStats } from '../utils/statistics';

const ACTION_TYPE_LABELS = {
  a: 'Att', d: 'Dif', c: 'Cnt', ca: 'Ca', ci: 'Ci', c2: 'C2'
};

function getPointsClass(pts) {
  if (pts >= 6)  return 'red';
  if (pts >= 3)  return 'orange';
  if (pts > 0)   return 'green';
  return '';
}

function TimelinePanel({ onDeleteAction, onLoadForEdit, onSeek }) {
  const { state } = useMatch();
  const { actions, penalties, session, currentRound, roundMarkers = [], editingActionId } = state;

  // Calcola l'intervallo del round corrente (se ha start+end marker)
  const roundStart = roundMarkers.find(m => m.round === currentRound && m.type === 'start');
  const roundEnd   = roundMarkers.find(m => m.round === currentRound && m.type === 'end');
  const isZoomed   = !!(roundStart && roundEnd);

  // Filtra eventi: se zoom attivo mostra solo quelli nel range temporale del round
  const filterByRound = (ev) => {
    if (!isZoomed) return true;
    const t = ev.timestamp_video;
    if (t == null) return true;
    return t >= roundStart.timestamp_video && t <= roundEnd.timestamp_video;
  };

  const roundActions   = actions.filter(filterByRound);
  const roundPenalties = penalties.filter(filterByRound);

  const blueStats = calculateAthleteStats(roundActions, 'blue');
  const redStats  = calculateAthleteStats(roundActions, 'red');

  const allEvents = [
    ...roundActions.map(a => ({ ...a, _type: 'action' })),
    ...roundPenalties.map(p => ({ ...p, _type: 'penalty' })),
  ].sort((a, b) => (b.timestamp_video || 0) - (a.timestamp_video || 0));

  // Colori e posizione per le sequenze taggate (calcolo in ordine cronologico)
  const SEQ_PALETTE = ['#9C27B0', 'var(--color-orange)', '#00BCD4', '#8BC34A', '#E91E63', '#FF5722'];
  const seqColorMap  = {};
  const seqPosMap    = {}; // evId -> posizione nella sequenza
  let _paletteIdx    = 0;
  const seqPosCount  = {};
  [...allEvents].reverse().forEach(ev => {
    if (!ev.sequence_id) return;
    if (!seqColorMap[ev.sequence_id]) {
      seqColorMap[ev.sequence_id] = SEQ_PALETTE[_paletteIdx++ % SEQ_PALETTE.length];
    }
    seqPosCount[ev.sequence_id] = (seqPosCount[ev.sequence_id] || 0) + 1;
    seqPosMap[ev.id] = seqPosCount[ev.sequence_id];
  });

  const blueName = session?.athlete_blue_name || 'BLU';
  const redName  = session?.athlete_red_name  || 'ROSSO';

  const handleDelete = (id) => {
    if (window.confirm('Eliminare questa azione?')) {
      onDeleteAction(id);
    }
  };

  return (
    <div className="panel" style={{ display: 'flex', flexDirection: 'column' }}>
      <div className="panel-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <span>Timeline & Statistiche</span>
        {isZoomed ? (
          <span style={{ fontSize: 10, color: 'var(--color-orange)', fontWeight: 600 }}>
            ⬛ R{currentRound} · {roundStart.timestamp_display} → {roundEnd.timestamp_display}
          </span>
        ) : (
          <span style={{ fontSize: 10, color: 'var(--color-text-dim)' }}>
            R{currentRound} · tutto il video
          </span>
        )}
      </div>

      {/* Timeline */}
      <div className="timeline-list" style={{ flex: 1, padding: '6px', minHeight: 0 }}>
        {allEvents.length === 0 && (
          <div style={{ textAlign: 'center', color: 'var(--color-text-dim)', marginTop: 20, fontSize: 12 }}>
            {isZoomed ? `Nessuna azione nel R${currentRound}` : 'Nessuna azione registrata'}
          </div>
        )}
        {allEvents.slice(0, 50).map((ev) => {
          const hasTimestamp = ev.timestamp_video != null && ev.timestamp_video > 0;

          if (ev._type === 'penalty') {
            return (
              <div
                key={`pen-${ev.id}`}
                className={`timeline-item ${ev.athlete}`}
                onClick={() => hasTimestamp && onSeek && onSeek(ev.timestamp_video)}
                style={{ cursor: hasTimestamp && onSeek ? 'pointer' : 'default' }}
              >
                <span className="timeline-time">{ev.timestamp_display || '--:--.-'}</span>
                <div className="timeline-content">
                  <div className={`timeline-athlete ${ev.athlete}`}>
                    {ev.athlete === 'blue' ? blueName : redName}
                  </div>
                  <div className="timeline-details">
                    Gam-jeom &mdash; {ev.penalty_type || ''}
                  </div>
                </div>
                <span className="timeline-points orange">GJ</span>
              </div>
            );
          }

          // Azioni locali = camelCase, remote = snake_case — normalizziamo
          const actionType  = ev.action_type  ?? ev.actionType;
          const techBonus   = ev.technical_bonus !== undefined ? ev.technical_bonus : ev.technicalBonus;
          const customTech  = ev.custom_technique ?? ev.customTechnique;
          const legSide     = ev.leg_side  ?? ev.legSide;
          const legPosition = ev.leg_position ?? ev.legPosition;
          const techLabel   = ev.technique === 'custom' ? (customTech || 'Custom') : ev.technique;
          const pts         = ev.points || 0;
          const seqColor    = ev.sequence_id ? seqColorMap[ev.sequence_id] : null;
          const seqPos      = ev.sequence_id ? seqPosMap[ev.id] : null;
          const isEditing   = ev.id === editingActionId;
          return (
            <div
              key={`act-${ev.id}`}
              className={`timeline-item ${ev.athlete}`}
              style={{
                cursor: 'pointer',
                ...(isEditing ? { outline: '1px solid #FF9800', background: '#FF980022' } : {}),
                ...(seqColor && !isEditing ? {
                  borderLeft: `3px solid ${seqColor}`,
                  paddingLeft: 4,
                  background: `${seqColor}18`,
                } : {}),
              }}
              onClick={() => onLoadForEdit && onLoadForEdit(ev)}
              title={isEditing ? 'In modifica…' : 'Clicca per modificare'}
            >
              <span className="timeline-time">{ev.timestamp_display || '--:--.-'}</span>
              <div className="timeline-content">
                <div className={`timeline-athlete ${ev.athlete}`}>
                  {ev.athlete === 'blue' ? blueName : redName}
                  {seqColor && (
                    <span style={{ color: seqColor, fontSize: 9, fontWeight: 700, marginLeft: 5 }}>
                      ⛓{seqPos}
                    </span>
                  )}
                </div>
                <div className="timeline-details">
                  R{ev.round_number} &middot; {ACTION_TYPE_LABELS[actionType] || actionType} &middot; {techLabel}
                  {ev.target && ` · ${ev.target}`}
                  {legSide && ` · ${legSide === 'sinistra' ? 'Sx' : 'Dx'}`}
                  {legPosition && ` · ${legPosition === 'anteriore' ? 'Ant.' : 'Post.'}`}
                  {techBonus ? ' · Rot.' : ''}
                </div>
              </div>
              <span className={`timeline-points ${getPointsClass(pts)}`}>
                {ev.outcome === 'punto' ? `+${pts}` : ev.outcome === 'tocco' ? '~' : 'X'}
              </span>
              {hasTimestamp && onSeek && (
                <button
                  className="timeline-delete"
                  onClick={(e) => { e.stopPropagation(); onSeek(ev.timestamp_video); }}
                  title="Vai al timestamp"
                >▶</button>
              )}
              <button
                className="timeline-delete"
                onClick={(e) => { e.stopPropagation(); handleDelete(ev.id); }}
                title="Elimina"
              >X</button>
            </div>
          );
        })}
      </div>

      {/* Stats */}
      <div className="stats-section">
        <div className="stats-athlete">
          <div className="stats-athlete-header blue">{blueName}</div>
          <div className="stats-row">
            <span className="stats-label">Azioni tot.</span>
            <span className="stats-value">{blueStats.totalActions}</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Eff. attacco</span>
            <span className="stats-value">{blueStats.attackEfficiency}%</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Res. difesa</span>
            <span className="stats-value">{blueStats.defenseEfficiency}%</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Punti totali</span>
            <span className="stats-value text-blue">{blueStats.totalPoints}</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Punti subiti</span>
            <span className="stats-value" style={{ color: 'var(--color-text-dim)' }}>{blueStats.pointsConceded}</span>
          </div>
        </div>
        <div className="stats-athlete">
          <div className="stats-athlete-header red">{redName}</div>
          <div className="stats-row">
            <span className="stats-label">Azioni tot.</span>
            <span className="stats-value">{redStats.totalActions}</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Eff. attacco</span>
            <span className="stats-value">{redStats.attackEfficiency}%</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Res. difesa</span>
            <span className="stats-value">{redStats.defenseEfficiency}%</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Punti totali</span>
            <span className="stats-value text-red">{redStats.totalPoints}</span>
          </div>
          <div className="stats-row">
            <span className="stats-label">Punti subiti</span>
            <span className="stats-value" style={{ color: 'var(--color-text-dim)' }}>{redStats.pointsConceded}</span>
          </div>
        </div>

        {/* Tecniche top */}
        {(blueStats.totalActions > 0 || redStats.totalActions > 0) && (
          <div style={{ marginTop: 6, fontSize: 10, color: 'var(--color-text-dim)' }}>
            <div>BLU: {Object.entries(blueStats.techniqueCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,v])=>`${k}x${v}`).join(' · ')}</div>
            <div>ROSSO: {Object.entries(redStats.techniqueCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,v])=>`${k}x${v}`).join(' · ')}</div>
          </div>
        )}

        {/* Tecniche subite top */}
        {(Object.keys(blueStats.techniquesReceivedCount).length > 0 || Object.keys(redStats.techniquesReceivedCount).length > 0) && (
          <div style={{ marginTop: 4, fontSize: 10, color: 'var(--color-text-dim)' }}>
            <div>BLU subisce: {Object.entries(blueStats.techniquesReceivedCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,v])=>`${k}x${v}`).join(' · ')}</div>
            <div>ROSSO subisce: {Object.entries(redStats.techniquesReceivedCount).sort((a,b)=>b[1]-a[1]).slice(0,3).map(([k,v])=>`${k}x${v}`).join(' · ')}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export default TimelinePanel;
